/**
 * @lodex-pro/web-portal/routes/MyRequests — the loads a pumper, foreman or admin turned in
 * (OPP PR 12a), newest first as the API returns them.
 *
 * Also lists the loads of the hauler requestor matched to the caller (OPP PR 10): those are not
 * `mine` and carry "Matched to you", since the hauler recorded them before (or outside) the
 * portal.
 *
 * Under OP_HAUL_REQUESTS + OP_ROUTES_PUMPERS; without them the page says so in plain words. The
 * API checks SUBMIT_HAUL_REQUEST and the features; this is the UX, not the gate.
 */

import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { PortalHaulRequestView } from '@lodex-pro/shared/validation/operator-portal-routes';
import { ApiError } from '@lodex-pro/web-shared';
import { usePortalMe } from '../hooks/useOperatorPortal';
import { useMyRequests } from '../hooks/useHaulRequests';
import { PortalDataError } from '../components/PortalDataError';
import { portalHaulRequestsOnPlan, portalRequestsHauls } from '../nav/portal-features';
import { MUTED, TAG, localDateTime } from './operator-tank-labels';
import { HaulRequestsOff } from './RequestHaul';

/** The marker on a load the caller's matched requestor turned in. */
export const MATCHED_TEXT = 'Matched to you';

const MATCHED_TAG: React.CSSProperties = {
  ...TAG,
  background: 'rgba(123, 150, 232, 0.18)',
  color: 'var(--color-accent-blue-dark)',
};
const TH: React.CSSProperties = { textAlign: 'left', padding: '8px 12px' };
const TD: React.CSSProperties = { padding: '8px 12px' };

/** The Operator API's five public statuses, in words. */
const STATUS_TEXT: Record<string, string> = {
  REQUESTED: 'Requested',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  REJECTED: 'Declined',
};

function statusText(status: string): string {
  return STATUS_TEXT[status] ?? status;
}

function when(iso: string | null): string {
  if (iso === null) return '—';
  const text = localDateTime(iso);
  return text === '' ? '—' : text;
}

function isFeatureOff(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'FEATURE_NOT_ENABLED';
}

export function MyRequests(): React.JSX.Element {
  const me = usePortalMe();
  if (me.data === undefined) {
    return (
      <main style={{ padding: '24px' }}>
        {me.isError ? (
          <p role="alert">Could not load your requests.</p>
        ) : (
          <p role="status">Loading…</p>
        )}
      </main>
    );
  }
  if (!portalRequestsHauls(me.data)) return <Navigate to="/portal" replace />;
  if (!portalHaulRequestsOnPlan(me.data)) return <HaulRequestsOff title="My requests" />;
  return <MyRequestsBody />;
}

function MyRequestsBody(): React.JSX.Element {
  const requests = useMyRequests();
  if (isFeatureOff(requests.error)) return <HaulRequestsOff title="My requests" />;
  const rows: readonly PortalHaulRequestView[] = requests.data?.requests ?? [];

  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
        <h1 style={{ margin: 0 }}>My requests</h1>
        <Link to="/portal/request-haul" style={{ color: 'var(--color-link)', fontSize: '14px' }}>
          Request a haul
        </Link>
      </div>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: '4px', marginBottom: '16px' }}>
        Loads you turned in, and loads a hauler has on record as requested by you.
      </p>

      {requests.isLoading && <p role="status">Loading requests…</p>}
      {requests.isError && (
        <PortalDataError error={requests.error} fallback="Could not load your requests." />
      )}

      {requests.data !== undefined && (
        <table
          aria-label="My requests"
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)',
          }}
        >
          <thead>
            <tr style={{ background: 'var(--color-bg-tertiary)' }}>
              <th style={TH}>Haul ID</th>
              <th style={TH}>Hauler</th>
              <th style={TH}>Site</th>
              <th style={TH}>Tanks</th>
              <th style={TH}>Status</th>
              <th style={TH}>Requested</th>
              <th style={TH}>Scheduled</th>
              <th style={TH}>Completed</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  No requests yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.jobId} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                  <td style={TD}>
                    {r.haulId ?? '—'}
                    {!r.mine && (
                      <span style={{ ...MATCHED_TAG, marginLeft: '8px' }}>{MATCHED_TEXT}</span>
                    )}
                  </td>
                  <td style={TD}>{r.haulerName}</td>
                  <td style={TD}>{r.siteName ?? '—'}</td>
                  <td style={TD}>{r.tankNames.length === 0 ? '—' : r.tankNames.join(', ')}</td>
                  <td style={TD}>{statusText(r.status)}</td>
                  <td style={TD}>{when(r.requestedAt)}</td>
                  <td style={TD}>{when(r.scheduledAt)}</td>
                  <td style={TD}>{when(r.completedAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </main>
  );
}
